import { useEffect, useState } from "react";
import { convertToDateString } from "../../../Helpers";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FieldTypes } from "../../interfaces/Global";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { PayoffQuoteLoanCommentForm, PayoffQuoteSettlementStatementForm } from "../ui/LmsSharedForms";
import LmsTable from "../ui/LmsTable";

const MainTable = () => {
  const actionButtons = [
    "edit",
    "export"
  ];

  return <LmsTable
    actionButtons={actionButtons}
    apiSuffix="payoffquotes/purchase-company-transactions"
    sortable={["Created On"]}
    columnClickOptions={{
      loan: {
        path: "servicing-purchase-company-transactions/edit",
        idProp: "id",
      },
      documentFileName: { downloadPath: "document/download", idProp: "documentLogId" },
    }}
    columnLabelsDictionary={{
      borrowerOrganization: "Borrower",
      pcmarkedPaidOff: "PC Marked Paid Off",
      pcpayoffFundsSentDate: "Payoff Funds Sent Date",
      pcpayoutAmountSent: "Payout Amount Sent",
      lptransactionAmount: "LP Transaction Amount",
      lptransactionEffDate: "LP Transaction Eff Date",
    }}
  />;
};

const editPurchaseCompanyTransactionsField = (
  form: BCFormController,
  pdrBankInfoList: any[]
) => {
  const handleDisplayRofr = () => {
    if (form?.initialValues) {
      const payoffQuote = form?.initialValues;
      if (payoffQuote.includeRofr) return true;
      if (payoffQuote.rofrpercent) return true;
      if (payoffQuote.roframount) return true;
      if (payoffQuote.customizeRofr) return true;
      if (payoffQuote.rofrpending) return true;
      if (payoffQuote.rofrcharged) return true;
      if (payoffQuote.rofrcustomAmount) return true;
    }
    return false;
  };

  if (handleDisplayRofr()) {
    form.addCenteredGroup([form.getLabelField("This loan has ROFR Language")]); // TODO: check with Slobodan on how to display big red text at top
  }

  form.addGroupFields([
    form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
      name: "specialPurposeEntityId",
      dependencyField: "borrowerOrganizationId" 
    }),
    form.getDisplayField("Loan", { name: "loanDisplay" }),
    form.getDisplayField("Escrow #", { name: "payoffEscrowNo" })
  ]);
  form.addGroupFields([
    form.getDropdownField("Fund", "metadata/list-organization-funds", { 
      name: "fundOrganizationId", 
      required: true,
      disabled: true
    }),
    form.getDropdownField("Bank", "orgs/list-bank-orgs", { name: "bankInformationId", required: true, disabled: true }), // TODO: Sprint Work 13367
    form.getCalendarField("Payout Date", { disabled: true }),
    form.getCalendarField("Valid Through Date", { disabled: true })
  ]);
  const refi = form?.initialValues?.refi === "No" ? false : true;
  form.addGroupFields([
    form.getCheckboxField("Refi?", { value: refi, inline: true }),
    form.getCurrencyField("Original Loan Amount", { name: "loanDetailLoanAmountOriginal", fieldType: "dollar", disabled: true }),
    form.getDisplayField("Days In Extension"),
  ]);

  form.addGroupFields([
    form.getCurrencyField("Current Loan Balance", { name: "loanAmountCurrent", required: true, disabled: true }),
    form.getInputField("Current Interest Rate", {
      name: "loanServicingInterestRateCurrent",
      disabled: true,
      required: true,
      fieldType: GlobalConstants.InputTypes.FLOAT as FieldTypes,
    }),
    form.getCurrencyField("Interest Reserve Balance", { required: true, disabled: true }),
  ]);

  let pdPeriod1;
  if (form?.initialValues?.pdStartDate1 && form?.initialValues?.pdEndDate1) {
    pdPeriod1 = `${convertToDateString(form?.initialValues?.pdStartDate1, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate1, "lmsdisplaydateforfieldname")}`;
  }

  let pdPeriod2;
  if (form?.initialValues?.pdStartDate2 && form?.initialValues?.pdEndDate2) {
    pdPeriod2 = `${convertToDateString(form?.initialValues?.pdStartDate2, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate2, "lmsdisplaydateforfieldname")}`;
  }

  form.addGroupFields([
    form.getCurrencyField(pdPeriod1 ? pdPeriod1 : "Interest Pdstart Pdend1", { name: "interestPdstartPdend1", required: true, disabled: true }),
    form.getCurrencyField(pdPeriod2 ? pdPeriod2 : "Interest Pdstart Pdend2", { name: "interestPdstartPdend2", required: true, disabled: true, isHidden: { saleTypeOption: "Sale to purchasing company" }, isHiddenOperation: "notequal" }), 
  ], { isHidden: { validThroughDate: "" }, isHiddenOperation: "notequal" });
  form.addGroupFields([
    form.getInputField("Per Diem Interest", { required: true }),
    form.getCheckboxField("Prorate Extension", { inline: true }),
    form.getCurrencyField("Extension Fee", { fieldType: "dollar", required: true, disabled: true }),
    form.getInputField("Per Diem Extension", { fieldType: "dollar", required: true }),
  ]);
  form.addGroupFields([
    form.getCheckboxField("Include Sweep", { inline: true }),
    form.getCurrencyField("Net roceeds", { fieldType: "dollar", disabled: true }), // TODO: ask Alex what display this field.
    form.getCurrencyField("Sweep Amount", { fieldType: "dollar", disabled: true }), // TODO: sprint work item 13370
    form.getDisplayField("Sweep % Amount", { name: "sweepPercentage", fieldType: "float" }),
  ]);
  form.addGroupFields([
    form.getCheckboxField("Include Additional Interest", { inline: true }),
    form.getDropdownField("Additional Interest Basis", "metadata/list-additional-interest-basis", { required: true, disabled: true }),
    form.getCurrencyField("Additional Interest Floor", { name: "additionalInterestAmount", required: true, disabled: true }),
  ]);
  form.addGroupFields([
    form.getCurrencyField("Reconveyance Fee", { required: true, disabled: true }),
    form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { name: "transactionTypeId" }),
    form.getCurrencyField("Additional Payoff Fees Amount", { name: "additionalPayoffFee" }),
  ]);
  form.addGroupFields([
    form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", value: form?.initialValues?.chargePayoffFee ? true : false, inline: true }),
    form.getCurrencyField("Charge Payoff Fee", { 
      isDisabledOn: { chargePayoffFeeCheckbox: true }, 
      isDisabledOnOperation: "notequal", 
    }),
    form.getCurrencyField("Previous Fee", { disabled: true }),
  ]);
  form.addGroupFields([
    form.getCurrencyField("Reduce Payoff Amount", { disabled: true }),
    form.getCheckboxField("Reduce By Remaining Budget", { disabled: true, inline: true }),
    form.getCurrencyField("Remining Budget", { disabled: true })
  ]);
  form.addGroupFields([
    form.getCheckboxField("Apply Extension Credit", { disabled: true, inline: true }),
    form.getCurrencyField("Extension Credit", { disabled: true }),
    form.getBlankField()
  ]);
  if (handleDisplayRofr()) {
    form.addGroupFields([
      form.getCheckboxField("Include ROFR", { disabled: true, inline: true }),
      form.getInputField("ROFR Percent", { disabled: true, fieldType: "float" }),
      form.getCurrencyField("ROFR Amount", { disabled: true })
    ]);
    form.addGroupFields([
      form.getCheckboxField("Customize ROFR", { disabled: true, inline: true }),
      form.getCurrencyField("ROFR Custom Amount", { isDisabledOn: { chargePayoffFeeCheckbox: true } })
    ]);
  }
  const payoutResidual = form?.initialValues?.totalAmount - form?.initialValues?.receivedAmount;
  form.addGroupFields([
    form.getCurrencyField("Total Payout", { disabled: true, name: "totalAmount" }),
    form.getCurrencyField("Payout Received", { disabled: true, name: "receivedAmount" }),
    form.getCurrencyField("Payout Residual", { disabled: true, value: payoutResidual })
  ]);
  if (form?.initialValues?.loanId) {
    form.addCenteredGroup([form.getLabelField("address")]);
    form.addComponent(
      <LmsTable
        apiSuffix={`payoffquotes/property-unit-reconveyance/${form?.initialValues.loanId}`}
        displayOnlyProps={["unitNumber", "actualSalesPrice"]}
        columnLabelsDictionary={{unitNumber: "Unit"}}
        isDisabled
      />
    );
  }
  form.addGroupFields([
    form.getTextareaField("Sweep Provision"),
    form.getTextareaField("Partial Deed Release Provision"),
  ]);

  form.addGroupFields([
    form.getComponent((options) =>
      <LmsTable
        apiSuffix={form?.values?.loanId ? `payoffquotes/payoff-temp-comments/${form?.values?.loanId}`: ""}
        displayOnlyProps={["commentSubjectDisplay", "description", "createdOn", "createdBy"]}
        columnLabelsDictionary={{commentSubjectDisplay: "Subject"}}
        title="Payoff comments"
        refreshDataDependency={options?.inputs?.lastAddedCommentId}
        compact
        isDisabled
      />,
    { width: 12 }),
    form.getButtonField("Add Comment", undefined, {
      modalContent: (setIsAddCommentModalOpen, options) => (
        <PayoffQuoteLoanCommentForm
          payoffQuoteOptions={options}
          id={form?.values?.loanId} 
          setIsAddCommentModalOpen={setIsAddCommentModalOpen} 
        />
      ),
      modalSize: "large",
      width: 4 
    })
  ], { blankWidth: 2 });

  form.addGroupFields([
    form.getDisplayField("Partial Deed Release Option"),
    form.getCurrencyField("Partial Deed Release Amount", { disabled: true }),
    form.getBlankField()
  ]);

  form.addGroupFields([
    form.getCurrencyField("Outstanding Interest And Fee Apply", { disabled: true }),
    form.getDropdownField("Residual Handling", "metadata/list-pc-residual-handling-options"),
    form.getDisplayField("", { name: "principalPaymentApply" }), //TODO: ask about field name
  ]);

  if (pdrBankInfoList) {
    form.addGroupFields([
      form.getDropdownField("PDR Bank Information", pdrBankInfoList, { name: "pdrBankInformationId" }),
      form.getCurrencyField("PDR Transaction Amount"),
      form.getCalendarField("PDR Transaction Effective Date")
    ]);
  }

  form.addCenteredGroup([form.getTextareaField("Comments / Instructions")]);
  form.addCenteredGroup([
    form.getButtonField("Settlement Statement / Escrow Request", undefined, {
      modalContent: (setIsModalOpen, options) => (
        <PayoffQuoteSettlementStatementForm
          id={form?.values?.loanId} 
          setIsPayoffQuoteDocumentModalOpen={setIsModalOpen} 
          payoffQuoteOptions={options}
        />
      )
    }),
    form.getComponent((options) => {
      return <div className="lms-flex-start-container">
        {options?.metaData?.uploadedSettlementStatements?.map((statement, i) => {
          return <a className="lms-download-form-field-link" key={`lms-download-form-field-link${i}`} onClick={() => {
            return options.handleDownloadFile(`payoffquotes/download-settlement-statements/${statement?.id}`, { fileName : statement?.assetFile?.name });
          }}>{statement?.assetFile?.name}</a>;
        })}
      </div>;
    })
  ]);
  
  return form;
};

const LmsServicingPurchaseCompanyTransactionsEditForm = ({ id }) => {
  const [{ data: purchaseCompanyTransactionsData, isLoading: isPurchaseCompanyTransactionsDataLoading, error, fetchData }] = useFetchData(`payoffquotes/purchase-company-transactions/${id}`);
  const [inputsCopy, setInputsCopy] = useState(undefined as any);
  const [PdrBankInfoList, setPdrBankInfoList] = useState(undefined as any);
  const myForm = editPurchaseCompanyTransactionsField(
    new BCFormController(purchaseCompanyTransactionsData),
    PdrBankInfoList
  );

  const formActions = [
    "save", 
    { text: "Mark Unit Paid Off", icon: GlobalConstants.Icons.SAVE, path: "payoffquotes/transaction/mark-unit-paid-off" }, 
    // TODO: Ask Alex when to show Mark Unit Paid off when to show Mark Paid off
    { text: "Mark Paid Off", icon: GlobalConstants.Icons.SAVE, path: "payoffquote/transaction/mark-loan-purchaser-paid-Off" }, 
    // TODO: Missing path, sprint work item 13368 ^
    { text: "Mark Loan Purchaser Paid Off", icon: GlobalConstants.Icons.SAVE, path: "payoffquote/transaction/mark-loan-purchaser-paid-Off" }, 
    // TODO: Ask Alex on what enable this button ^
    "close"
  ];

  const onPayoffTranactionsFormChange = (options) => {
    setInputsCopy({...options?.inputs});
  };

  useEffect(() => {
    if (purchaseCompanyTransactionsData) {
      if (purchaseCompanyTransactionsData?.fundOrganizationId) {
        const getPdrBankInfoList = async () => {
          const { data: bankList } = await fetchData(`payoffquotes/list-fund-bank-info/${purchaseCompanyTransactionsData?.fundOrganizationId}`, { fetchMethod: "GET" });
          setPdrBankInfoList(bankList);
        };
        
        getPdrBankInfoList();
      }
    }
  }, [purchaseCompanyTransactionsData]);

  return <LmsFormGenerator
    myForm={myForm} 
    isLoading={isPurchaseCompanyTransactionsDataLoading} 
    error={error} 
    actions={formActions} 
    inputs={inputsCopy}
    onFormChanged={onPayoffTranactionsFormChange}
  />;
};

const LmsServicingPurchaseCompanyTransactions = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(params);
  if (!isActionAllowed) {
    history.goBack();
  }

  return (<>
    {isMainPage && <MainTable />}
    {isEdit && <LmsServicingPurchaseCompanyTransactionsEditForm id={id} />}
  </>);
};

export default LmsServicingPurchaseCompanyTransactions;